// Session bootstrap: online → /auth/me; offline → the last authenticated session on this device (offline use
// requires a prior authenticated initialization). Session pointers live in a small IndexedDB database
// (ids and display names only — never tokens; the session cookie is HttpOnly).
import { openDB } from 'idb';
import { me as fetchMe, type Me } from '@/api/auth';
import { TransportError } from './ports';
import { uuidv7 } from './ids';

export interface SessionInfo {
  brandId: string; brandName: string; userId: string; email: string; displayName: string; color: string; username?: string | null; photo?: string | null;
  workspaceId: string; workspaces: Me['workspaces']; deviceId: string; offline: boolean;
}
export type BootResult = { kind: 'signed_out' } | { kind: 'ready'; session: SessionInfo } | { kind: 'unavailable_offline' } | { kind: 'maintenance' };

const SESS_DB = 'taskpanes|sessions|' + encodeURIComponent(location.origin);
const sessDb = () => openDB(SESS_DB, 1, { upgrade(db) { db.createObjectStore('kv'); } });

export async function deviceId(): Promise<string> {
  const db = await sessDb();
  let id = (await db.get('kv', 'device_id')) as string | undefined;
  if (!id) { id = uuidv7(); await db.put('kv', id, 'device_id'); }
  return id;
}

export async function rememberSession(s: SessionInfo): Promise<void> {
  const { offline: _o, ...rest } = s;
  await (await sessDb()).put('kv', rest, 'last_session');
}
export async function forgetSession(): Promise<void> { await (await sessDb()).delete('kv', 'last_session'); }

export async function sessionFromMe(m: Me): Promise<SessionInfo> {
  const db = await sessDb();
  const last = (await db.get('kv', 'last_session')) as SessionInfo | undefined;
  // Stable deep links: /w/{workspaceId}[/p/{projectId}[/t/{taskId}]] select the workspace (never carry tokens).
  const linked = /^\/w\/([0-9a-f-]{36})(?:\/|$)/.exec(location.pathname)?.[1];
  const preferred = linked && m.workspaces.some(w => w.id === linked) ? linked
    : last && last.userId === m.user.id && m.workspaces.some(w => w.id === last.workspaceId) ? last.workspaceId : m.workspaces[0]?.id;
  if (!preferred) throw new Error('No workspace available for this account');
  const s: SessionInfo = { brandId: m.brand.id, brandName: m.brand.display_name, userId: m.user.id, email: m.user.email, displayName: m.user.display_name, color: m.user.avatar_color || '#5b8def', username: m.user.username ?? null, photo: m.user.photo_url ?? null, workspaceId: preferred, workspaces: m.workspaces, deviceId: await deviceId(), offline: false };
  await rememberSession(s);
  return s;
}

export async function boot(): Promise<BootResult> {
  try {
    return { kind: 'ready', session: await sessionFromMe(await fetchMe()) };
  } catch (e) {
    if (e instanceof TransportError && e.kind === 'auth') return { kind: 'signed_out' };
    if (e instanceof TransportError && (e.kind === 'network' || e.kind === 'server')) {
      const last = (await (await sessDb()).get('kv', 'last_session')) as SessionInfo | undefined;
      // Maintenance (503 `maintenance` for a signed-in non-staff account): with a cached workspace the app opens it
      // offline (local edits keep queueing); without one, say so instead of "offline".
      if (!last) return { kind: e.code === 'maintenance' ? 'maintenance' : 'unavailable_offline' };
      const linked = /^\/w\/([0-9a-f-]{36})(?:\/|$)/.exec(location.pathname)?.[1];
      const ws = linked && last.workspaces.some(w => w.id === linked) ? linked : last.workspaceId;
      return { kind: 'ready', session: { ...last, workspaceId: ws, offline: true } };
    }
    throw e;
  }
}
